import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import Index from "./pages/Index";

// Lazy-load non-landing routes to reduce initial JS for slow connections
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminWebhooks = lazy(() => import("./pages/AdminWebhooks"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const Settings = lazy(() => import("./pages/Settings"));
const Deposits = lazy(() => import("./pages/Deposits"));
const DepositHistory = lazy(() => import("./pages/DepositHistory"));
const Transfer = lazy(() => import("./pages/Transfer"));
const Support = lazy(() => import("./pages/Support"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/webhooks" element={<AdminWebhooks />} />
              <Route path="/admin/audit" element={<AdminAuditLog />} />
              <Route path="/admin/support" element={<AdminSupport />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/deposits" element={<Deposits />} />
              <Route path="/deposits/history" element={<DepositHistory />} />
              <Route path="/transfer" element={<Transfer />} />
              <Route path="/support" element={<Support />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
